package com.dualremotetest

import kotlin.math.abs

// Enemy navigation and decisions.
//
// Verified original behaviour (ROM notes) that this keeps: enemies only change direction at 8-px grid points,
// re-decide now and then rather than every frame, turn away when blocked, fire at random (≈1/32 per frame) with one
// bullet each, and over the course of a stage shift between wandering, chasing a player and heading for the eagle.
// Our adaptation: instead of the ROM's random walk with a direction bias, each enemy keeps a goal for several
// seconds and follows a distance map on the 8-px grid. Brick counts as passable at a cost (the enemy shoots its way
// through), steel and water do not; the maps are rebuilt whenever terrain changes, so destroyed brick opens routes.

const val NODES = 25

enum class Lane(val label: String, val face: Dir) { TOP("top", Dir.DOWN), LEFT("left", Dir.RIGHT), RIGHT("right", Dir.LEFT) }

enum class GoalKind { ROAM, PLAYER, HQ }

class EnemyBrain {
    var goal = GoalKind.ROAM
    var lane = Lane.TOP
    var player = 0
    var roamNode = 0
    var goalUntil = 0
    /** At its HQ firing lane: stays put, facing the eagle. */
    var holding = false
    /** The previous goal ended at an HQ lane: the next one is not the HQ again straight away. */
    var justHeld = false
    var blocked = 0
    var bannedDir: Dir? = null
    var bannedUntil = 0
    var randomUntil = 0
    var node = -1
    var nodeSince = 0
    var decX = -1
    var decY = -1
    var stuckEvents = 0
    var decisions = 0
}

class Navigator(private val terrain: Terrain) {
    private var builtFor = -1
    private val passable = BooleanArray(NODES * NODES)
    /** Brick cells in the 8-px strip a tank enters when moving into node n from direction d, at [n * 4 + d]. */
    private val brickIn = IntArray(NODES * NODES * 4)
    private val cache = HashMap<Int, IntArray>()
    var rebuilds = 0
        private set
    var fieldsBuilt = 0
        private set

    fun refresh() {
        if (builtFor == terrain.version) return
        builtFor = terrain.version
        rebuilds++
        cache.clear()
        for (j in 0 until NODES) for (i in 0 until NODES) {
            val x = i * 8
            val y = j * 8
            val n = j * NODES + i
            passable[n] = !terrain.any(x, y, TANK, TANK) { it == T.STEEL || it == T.WATER } && !overlapsHq(x, y)
            for (d in Dir.entries) {
                brickIn[n * 4 + d.ordinal] = when (d) {
                    Dir.UP -> terrain.count(x, y, 16, 8, T.BRICK)
                    Dir.DOWN -> terrain.count(x, y + 8, 16, 8, T.BRICK)
                    Dir.LEFT -> terrain.count(x, y, 8, 16, T.BRICK)
                    Dir.RIGHT -> terrain.count(x + 8, y, 8, 16, T.BRICK)
                }
            }
        }
    }

    fun passable(n: Int) = n in 0 until NODES * NODES && passable[n]

    fun neighbour(n: Int, d: Dir): Int {
        val i = n % NODES + d.dx
        val j = n / NODES + d.dy
        return if (i < 0 || j < 0 || i >= NODES || j >= NODES) -1 else j * NODES + i
    }

    /** Nearest passable grid node to a tank position (which need not be aligned). */
    fun nearestNode(x: Int, y: Int): Int {
        val i0 = (x / 8).coerceIn(0, NODES - 1)
        val j0 = (y / 8).coerceIn(0, NODES - 1)
        var best = j0 * NODES + i0
        var bestD = Int.MAX_VALUE
        for (j in j0..minOf(j0 + 1, NODES - 1)) for (i in i0..minOf(i0 + 1, NODES - 1)) {
            val n = j * NODES + i
            val d = abs(i * 8 - x) + abs(j * 8 - y) + if (passable[n]) 0 else 1000
            if (d < bestD) { bestD = d; best = n }
        }
        return best
    }

    /** Cost of moving one 8-px step into [to] in direction [d]: 4, plus a penalty when brick must be shot away. */
    fun stepCost(to: Int, d: Dir): Int {
        val b = brickIn[to * 4 + d.ordinal]
        return 4 + if (b > 0) 6 + b * 3 else 0
    }

    fun lane(lane: Lane): IntArray = field(1000 + lane.ordinal) { laneSources(lane) }
    fun toNode(n: Int): IntArray = field(2000 + n) { listOf(n) }
    fun fromPlayerSpawns(): IntArray = field(3000) { Spots.PLAYER_X.map { it / 8 + (Spots.PLAYER_Y / 8) * NODES } }

    /** Grid nodes from which a tank facing the eagle can hit it with no steel in the way (brick may be shot through). */
    fun laneSources(lane: Lane): List<Int> {
        val out = ArrayList<Int>()
        when (lane) {
            Lane.TOP -> for (x in intArrayOf(88, 96)) for (y in 64..168 step 8) addIfClear(out, x, y, lane)
            Lane.LEFT -> for (y in intArrayOf(184, 192)) for (x in 0..72 step 8) addIfClear(out, x, y, lane)
            Lane.RIGHT -> for (y in intArrayOf(184, 192)) for (x in 120..192 step 8) addIfClear(out, x, y, lane)
        }
        return out
    }

    private fun addIfClear(out: MutableList<Int>, x: Int, y: Int, lane: Lane) {
        val n = (y / 8) * NODES + x / 8
        if (passable[n] && fireLineClear(terrain, x, y, lane.face, Spots.HQ_X, Spots.HQ_Y)) out += n
    }

    private fun field(key: Int, sources: () -> List<Int>): IntArray {
        refresh()
        cache[key]?.let { return it }
        if (cache.size > 40) cache.clear()
        val f = dijkstra(sources())
        cache[key] = f
        fieldsBuilt++
        return f
    }

    private fun dijkstra(sources: List<Int>): IntArray {
        val dist = IntArray(NODES * NODES) { INF }
        val q = LongHeap()
        for (s in sources) if (passable[s] && dist[s] != 0) {
            dist[s] = 0
            q.add(s.toLong())
        }
        while (q.isNotEmpty()) {
            val e = q.poll()!!
            val d = (e shr 16).toInt()
            val v = (e and 0xffff).toInt()
            if (d != dist[v]) continue
            // Reverse edges: u is a node from which one step in direction dir enters v.
            for (dir in Dir.entries) {
                val u = neighbour(v, dir.opposite)
                if (u < 0 || !passable[u]) continue
                val nd = d + stepCost(v, dir)
                if (nd < dist[u]) {
                    dist[u] = nd
                    q.add((nd.toLong() shl 16) or u.toLong())
                }
            }
        }
        return dist
    }

    companion object {
        const val INF = Int.MAX_VALUE / 4

        fun overlapsHq(x: Int, y: Int) = x < Spots.HQ_X + 16 && Spots.HQ_X < x + 16 && y < Spots.HQ_Y + 16 && Spots.HQ_Y < y + 16
    }
}

/**
 * True when a bullet fired by a tank at ([tx],[ty]) facing [d] reaches the 16×16 target at ([ox],[oy]) without
 * meeting steel on its stopping probes (brick and water are allowed: brick is shot through, water is flown over).
 */
fun fireLineClear(terrain: Terrain, tx: Int, ty: Int, d: Dir, ox: Int, oy: Int): Boolean {
    var bx = tx + 8 + d.dx * 8
    var by = ty + 8 + d.dy * 8
    val hits = if (d.isHorizontal) by in oy..(oy + 16) else bx in ox..(ox + 16)
    if (!hits) return false
    while (bx in 0 until FIELD && by in 0 until FIELD) {
        if (bx in ox until ox + 16 && by in oy until oy + 16) return true
        val a = terrain.at(bx, by)
        val c = if (d.isHorizontal) terrain.at(bx, by - 1) else terrain.at(bx - 1, by)
        if (a == T.STEEL || c == T.STEEL) return false
        bx += d.dx * 2
        by += d.dy * 2
    }
    return false
}

/** Runs the enemies for [TankMatch]; split out so the decision rules can be read (and tested) on their own. */
class EnemyAi(private val m: TankMatch) {
    var stuckEvents = 0
        private set
    var laneArrivals = 0
        private set
    val goalCounts = IntArray(GoalKind.entries.size)
    val laneCounts = IntArray(Lane.entries.size)

    fun reset() {
        stuckEvents = 0
        laneArrivals = 0
        goalCounts.fill(0)
        laneCounts.fill(0)
    }

    fun update(e: EnemyTank) {
        val b = e.brain
        val nav = m.nav
        val node = nav.nearestNode(e.x, e.y)
        if (node != b.node) {
            b.node = node
            b.nodeSince = m.stageFrame
        } else if (!b.holding && m.stageFrame - b.nodeSince > STUCK_FRAMES) {
            // Safety net: an enemy that has not reached another grid node for 10 s wanders at random for a while.
            b.stuckEvents++
            stuckEvents++
            b.randomUntil = m.stageFrame + 120
            b.nodeSince = m.stageFrame
            m.event("enemy #${e.id} ${e.type.label} made no progress for ${STUCK_FRAMES / FPS} s at node $node – random walk")
        }
        if (b.holding) {
            // Re-check the lane now and then (goal timer, terrain or shovel steel may have changed it).
            if (m.stageFrame % 16 == 0) decide(e, node)
            if (b.holding) {
                if (e.dir != b.lane.face) e.dir = b.lane.face
                return
            }
        } else if (e.x % 8 == 0 && e.y % 8 == 0 && (e.x != b.decX || e.y != b.decY)) {
            // One decision per grid point reached, so a slow tank sitting on a point for two frames cannot dither.
            b.decX = e.x
            b.decY = e.y
            decide(e, node)
            if (b.holding) return
        }
        val before = e.x to e.y
        m.advance(e, e.type.speedQ)
        if (m.lastStepBlocked) onBlocked(e) else if (before != (e.x to e.y)) b.blocked = 0
    }

    private fun decide(e: EnemyTank, node: Int) {
        val b = e.brain
        val nav = m.nav
        if (m.stageFrame >= b.goalUntil) chooseGoal(e, node)
        val field = goalField(b)
        if (b.goal == GoalKind.HQ && field != null && field[node] == 0) {
            if (!b.holding) {
                laneArrivals++
                // Adaptation: it stops to shoot at the HQ for a few seconds, then moves on (the ROM AI never stops).
                b.goalUntil = minOf(b.goalUntil, m.stageFrame + HOLD_FRAMES + m.rng.nextInt(HOLD_FRAMES))
            }
            b.holding = true
            return
        }
        b.holding = false
        if (field != null && field[node] == 0 || field != null && field[node] >= Navigator.INF) {
            // Arrived (player / roam goal) or the goal became unreachable: pick a new one now.
            chooseGoal(e, node)
        }
        b.decisions++
        val options = Dir.entries.filter { d ->
            val nb = nav.neighbour(node, d)
            nb >= 0 && nav.passable(nb) && !(d == b.bannedDir && m.stageFrame < b.bannedUntil)
        }.ifEmpty { Dir.entries.filter { d -> nav.neighbour(node, d).let { it >= 0 && nav.passable(it) } } }
        if (options.isEmpty()) return
        // A player in plain sight along a free line draws attention (no aiming at hidden players).
        sightedPlayerDir(e)?.let { d ->
            if (d in options && m.rng.nextInt(2) == 0) {
                m.turn(e, d)
                return
            }
        }
        val f = goalField(b)
        val random = m.stageFrame < b.randomUntil || m.rng.nextInt(RANDOM_TURN_ONE_IN) == 0 || f == null
        val choice = if (random) {
            val forward = options.filter { it != e.dir.opposite }.ifEmpty { options }
            forward[m.rng.nextInt(forward.size)]
        } else {
            options.minByOrNull { d ->
                val nb = nav.neighbour(node, d)
                val turn = when (d) {
                    e.dir -> 0
                    e.dir.opposite -> 24
                    else -> 3
                }
                f!![nb].toLong() + nav.stepCost(nb, d) + turn + m.rng.nextInt(4)
            }!!
        }
        m.turn(e, choice)
    }

    private fun goalField(b: EnemyBrain): IntArray? {
        val nav = m.nav
        val f = when (b.goal) {
            GoalKind.HQ -> if (m.hqAlive) nav.lane(b.lane) else null
            GoalKind.PLAYER -> m.knownNode(b.player)?.let { nav.toNode(it) }
            GoalKind.ROAM -> nav.toNode(b.roamNode)
        }
        return f
    }

    private fun chooseGoal(e: EnemyTank, node: Int) {
        val b = e.brain
        val nav = m.nav
        b.justHeld = b.holding
        b.holding = false
        b.goalUntil = m.stageFrame + 360 + m.rng.nextInt(480)
        // ROM phases (k = stage frames / 64, S = spawn delay): random until k > S/8, then toward a player, and toward the
        // eagle once k > S/4 (stage 1: about 25.6 s and 50 s). Adaptation: each phase only shifts the odds, so the
        // enemies never all do the same thing.
        val k = m.stageFrame / 64
        val (hqOdds, player) = when {
            k <= m.spawnDelay / 8 -> 15 to 25
            k <= m.spawnDelay / 4 -> 25 to 50
            else -> 55 to 25
        }
        val hq = if (m.runDifficulty == Difficulty.EASY) hqOdds / 2 else hqOdds // EASY (ours): half as many HQ attacks
        val roll = m.rng.nextInt(100)
        val reachableLanes = if (m.hqAlive) Lane.entries.filter { nav.lane(it)[node] < Navigator.INF } else emptyList()
        val visiblePlayers = m.players.filter { m.isParticipant(it) && it.inPlay && m.knownNode(it.index) != null }
        when {
            roll < hq && reachableLanes.isNotEmpty() && !b.justHeld -> {
                b.goal = GoalKind.HQ
                b.goalUntil = m.stageFrame + 300 + m.rng.nextInt(300)
                // Usually the nearest firing lane, sometimes any reachable one, so the attack comes from varied sides.
                b.lane = if (m.rng.nextInt(2) == 0) reachableLanes.minBy { nav.lane(it)[node] }
                else reachableLanes[m.rng.nextInt(reachableLanes.size)]
                laneCounts[b.lane.ordinal]++
            }
            roll < hq + player && visiblePlayers.isNotEmpty() -> {
                b.goal = GoalKind.PLAYER
                b.player = visiblePlayers[m.rng.nextInt(visiblePlayers.size)].index
            }
            else -> {
                b.goal = GoalKind.ROAM
                b.roamNode = randomRoamNode(node)
            }
        }
        goalCounts[b.goal.ordinal]++
    }

    private fun randomRoamNode(from: Int): Int {
        val nav = m.nav
        val reach = nav.toNode(from)
        repeat(40) {
            // Two thirds of roam targets lie in the lower half, which keeps pressure moving toward the players' side.
            val j = if (m.rng.nextInt(3) < 2) 12 + m.rng.nextInt(NODES - 12) else m.rng.nextInt(NODES)
            val n = j * NODES + m.rng.nextInt(NODES)
            if (nav.passable(n) && reach[n] < Navigator.INF) return n
        }
        return from
    }

    private fun onBlocked(e: EnemyTank) {
        val b = e.brain
        b.blocked++
        val front = m.frontStrip(e)
        val brick = m.terrain.brickTileIn(front[0], front[1], front[2], front[3])
        val tank = m.tankAhead(e)
        if (brick && m.canEnemyFire(e) && m.rng.nextInt(4) == 0) m.enemyFire(e)
        val patience = when {
            tank -> 20 + m.rng.nextInt(20)
            brick -> 150
            else -> 1
        }
        if (b.blocked < patience) return
        // ROM rule when blocked: 25% reverse, otherwise a random quarter turn (only directions that can move).
        b.bannedDir = e.dir
        b.bannedUntil = m.stageFrame + 90
        b.blocked = 0
        val sides = listOf(e.dir.cw(), e.dir.ccw()).filter { m.canTurnAndStep(e, it) }
        val next = when {
            m.rng.nextInt(4) == 0 && m.canTurnAndStep(e, e.dir.opposite) -> e.dir.opposite
            sides.isNotEmpty() -> sides[m.rng.nextInt(sides.size)]
            m.canTurnAndStep(e, e.dir.opposite) -> e.dir.opposite
            else -> return
        }
        m.turn(e, next)
    }

    /** Direction of a visible, active player in a straight free line from this enemy, if any. */
    fun sightedPlayerDir(e: EnemyTank): Dir? {
        for (p in m.players) {
            if (!m.isParticipant(p) || !p.inPlay || m.hiddenFromEnemies(p)) continue
            for (d in Dir.entries) {
                val ahead = when (d) {
                    Dir.UP -> p.cy < e.cy
                    Dir.DOWN -> p.cy > e.cy
                    Dir.LEFT -> p.cx < e.cx
                    Dir.RIGHT -> p.cx > e.cx
                }
                val dist = abs(p.cx - e.cx) + abs(p.cy - e.cy)
                if (ahead && dist < SIGHT_PX && fireLineClear(m.terrain, e.x, e.y, d, p.x, p.y)) return d
            }
        }
        return null
    }

    companion object {
        const val STUCK_FRAMES = 600
        const val HOLD_FRAMES = 150
        const val RANDOM_TURN_ONE_IN = 12
        const val SIGHT_PX = 120
    }
}

fun Dir.cw(): Dir = Dir.entries[(ordinal + 3) % 4]
fun Dir.ccw(): Dir = Dir.entries[(ordinal + 1) % 4]
